import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Event,
  CreditCard,
  LocationOn,
  School,
  Analytics,
  Warning,
  Person,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import JuntoApiService from '../services/api';
import { useAuth } from '../contexts/ClerkAuthContext';

interface DashboardStats {
  totalUsers: number;
  totalInstructors: number;
  totalClasses: number;
  totalCredits: number;
  totalLocations: number;
  totalRooms: number;
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalInstructors: 0,
    totalClasses: 0,
    totalCredits: 0,
    totalLocations: 0,
    totalRooms: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiServerInfo, setApiServerInfo] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadDashboardData();
    detectApiServer();
  }, []);

  const detectApiServer = async () => {
    try {
      // Get the actual API URL from the service
      const apiUrl = JuntoApiService.getBaseUrl();
      setApiServerInfo(apiUrl);
    } catch (err) {
      setApiServerInfo('API server connection failed');
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load data from all endpoints in parallel
      const [
        users,
        instructors,
        classes,
        credits,
        locations,
        rooms,
      ] = await Promise.all([
        JuntoApiService.getUsers().catch(() => []),
        JuntoApiService.getInstructors().catch(() => []),
        JuntoApiService.getClasses().catch(() => []),
        JuntoApiService.getCredits().catch(() => []),
        JuntoApiService.getLocations().catch(() => []),
        JuntoApiService.getRooms().catch(() => []),
      ]);

      setStats({
        totalUsers: users.length,
        totalInstructors: instructors.length,
        totalClasses: classes.length,
        totalCredits: credits.length,
        totalLocations: locations.length,
        totalRooms: rooms.length,
      });
      
      setError(null);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Please check the API connection.');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Create New Class',
      description: 'Schedule a new class session',
      icon: <Event />,
      action: () => navigate('/classes'),
      color: '#1976d2',
    },
    {
      title: 'Add User',
      description: 'Register a new user',
      icon: <People />,
      action: () => navigate('/users'),
      color: '#388e3c',
    },
    {
      title: 'Manage Credit Packages',
      description: 'Create or edit credit packages',
      icon: <CreditCard />,
      action: () => navigate('/credit-packages'),
      color: '#f57c00',
    },
    {
      title: 'View Analytics',
      description: 'See performance reports',
      icon: <Analytics />,
      action: () => navigate('/analytics'),
      color: '#7b1fa2',
    },
  ];

  const statCards = [
    { title: 'Total Users', value: stats.totalUsers, icon: <People />, color: '#1976d2' },
    { title: 'Instructors', value: stats.totalInstructors, icon: <School />, color: '#388e3c' },
    { title: 'Upcoming Classes', value: stats.totalClasses, icon: <Event />, color: '#f57c00' },
    { title: 'Credit Types', value: stats.totalCredits, icon: <CreditCard />, color: '#7b1fa2' },
    { title: 'Locations', value: stats.totalLocations, icon: <LocationOn />, color: '#c62828' },
    { title: 'Rooms', value: stats.totalRooms, icon: <LocationOn />, color: '#2e7d32' },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Junto Pay Management Console
          </Typography>
        </Box>
        
        {/* User Info */}
        <Box textAlign="right">
          {user && (
            <>
              <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" mb={1}>
                {user.imageUrl && (
                  <img 
                    src={user.imageUrl} 
                    alt={user.firstName || 'User'} 
                    style={{ width: 32, height: 32, borderRadius: '50%' }}
                  />
                )}
                <Typography variant="body1" fontWeight="500">
                  {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                </Typography>
              </Box>
              <Chip
                icon={user.isAdmin ? <AdminPanelSettings /> : <Person />}
                label={user.isAdmin ? 'Admin' : 'User'}
                color={user.isAdmin ? 'primary' : 'default'}
                size="small"
              />
            </>
          )}
        </Box>
      </Box>

      {/* API Server Info */}
      {apiServerInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Connected to: {apiServerInfo}
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center">
            <Warning sx={{ mr: 1 }} />
            {error}
            <Button 
              size="small" 
              onClick={loadDashboardData}
              sx={{ ml: 2 }}
            >
              Retry
            </Button>
          </Box>
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Box 
                    sx={{ 
                      backgroundColor: stat.color, 
                      color: 'white', 
                      borderRadius: '50%', 
                      p: 1, 
                      mr: 2 
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" component="div">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Common administrative tasks
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Box 
                          sx={{ 
                            backgroundColor: action.color, 
                            color: 'white', 
                            borderRadius: '50%', 
                            p: 1, 
                            mr: 2 
                          }}
                        >
                          {action.icon}
                        </Box>
                        <Typography variant="h6" component="div">
                          {action.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {action.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={action.action}>
                        Go
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* System Status */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <TrendingUp color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="API Status" 
                  secondary="Connected"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <Event color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Next 7 Days" 
                  secondary={`${stats.totalClasses} scheduled classes`}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <People color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Active Users" 
                  secondary={`${stats.totalUsers} registered`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}